import { WARHOL_DOCUMENT_EXTENSIONS } from "@mg/schemas/src/christo/askPuntt";
import { useCallback, useRef, useState } from "react";

export type UseFileDropOptions = {
  onDrop: (files: File[]) => void;
  accept?: string[];
};

export const ACCEPTED_FILE_TYPES = [
  "image/*",
  "video/*",
  ".ai",
  ".pdf",
  ".doc",
  ".docx",
  ".xls",
  ".xlsx",
  ".ppt",
  ".pptx",
  ".txt",
  ".rtf",
  ".csv",
  ".mp4",
  ".mov",
  ".webm",
  ".zip",
];

/**
 * The raster image shapes the warhol composer offers, as dotted extensions.
 * christo's `WARHOL_DOCUMENT_EXTENSIONS` gates on extension, not on the
 * `image/*` MIME wildcard, so the picker cannot offer a bare `image/*` -- that
 * lets through svg / heic / avif / ico, which christo then refuses with a 400.
 * Derived from the shared set (not a hand-kept copy) so the picker can never
 * advertise a raster shape the send would bounce.
 */
const WARHOL_IMAGE_EXTENSIONS = [
  "png",
  "jpg",
  "jpeg",
  "tif",
  "tiff",
  "bmp",
  "gif",
  "webp",
]
  .filter((ext) => WARHOL_DOCUMENT_EXTENSIONS.has(ext))
  .map((ext) => `.${ext}`);

/**
 * What a warhol chat's composer offers: the document shapes warhol opens
 * (PDF, images, office, text) and the legacy office shapes samba converts for
 * it. No video, zip or rtf -- christo refuses those on a warhol chat with a
 * 400 (`warholDocumentsFor`), so the picker must not offer what the send
 * would reject.
 *
 * Built off christo's `WARHOL_DOCUMENT_EXTENSIONS` (shared from `@mg/schemas`),
 * so the picker list and the server's 400 gate cannot drift: an entry the
 * composer offers survives only if the shared set still opens it, and the
 * `image/*` wildcard is swapped for the concrete raster shapes in that set so
 * an svg / heic drop is rejected up front rather than after an upload the send
 * bounces. `.ai` is a vector shape christo opens but the FE keeps its historic
 * dotted spelling; every offered entry is a subset of the shared set.
 */
export const WARHOL_ACCEPTED_FILE_TYPES = [
  ...ACCEPTED_FILE_TYPES.filter(
    (pattern) =>
      !pattern.endsWith("/*") &&
      WARHOL_DOCUMENT_EXTENSIONS.has(pattern.replace(/^\./, "").toLowerCase()),
  ),
  ...WARHOL_IMAGE_EXTENSIONS,
];

export function fileMatchesAccept(file: File, patterns: string[]): boolean {
  return patterns.some((pattern) => {
    if (pattern.endsWith("/*")) {
      return file.type.startsWith(pattern.replace("/*", "/"));
    }
    return file.name.toLowerCase().endsWith(pattern.toLowerCase());
  });
}

/**
 * Provides drag-and-drop file support for a container element.
 * Also provides a paste handler for clipboard image support.
 *
 * Usage:
 *   const { isDragOver, dropZoneProps, handlePaste } = useFileDrop({ onDrop: addFiles });
 *   <div {...dropZoneProps}> ... <TextArea onPaste={handlePaste} /> </div>
 */
export function useFileDrop(options: UseFileDropOptions) {
  const { onDrop, accept = ACCEPTED_FILE_TYPES } = options;
  const [isDragOver, setIsDragOver] = useState(false);
  const dragCounter = useRef(0);

  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current++;
    if (e.dataTransfer.types.includes("Files")) {
      setIsDragOver(true);
    }
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current = Math.max(0, dragCounter.current - 1);
    if (dragCounter.current === 0) {
      setIsDragOver(false);
    }
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      dragCounter.current = 0;
      setIsDragOver(false);

      const files = Array.from(e.dataTransfer.files).filter((f) =>
        fileMatchesAccept(f, accept),
      );
      if (files.length > 0) {
        onDrop(files);
      }
    },
    [onDrop, accept],
  );

  const handlePaste = useCallback(
    (e: React.ClipboardEvent) => {
      const items = Array.from(e.clipboardData.items);
      const files = items
        .filter((item) => item.kind === "file")
        .map((item) => item.getAsFile())
        .filter((f): f is File => f !== null && fileMatchesAccept(f, accept));

      if (files.length > 0) {
        onDrop(files);
      }
    },
    [onDrop, accept],
  );

  return {
    isDragOver,
    dropZoneProps: {
      onDragEnter: handleDragEnter,
      onDragLeave: handleDragLeave,
      onDragOver: handleDragOver,
      onDrop: handleDrop,
    },
    handlePaste,
  };
}
